import Phaser from 'phaser';

import type { LaneChooserGameTheme, LaneChooserSkyCloud } from '../types';

type SkyCloudRuntime = {
	sprite: Phaser.GameObjects.Image;
	startX: number;
	endX: number;
	durationMs: number;
	progress: number;
};

type SkyControllerConfig = {
	scene: Phaser.Scene;
	container: Phaser.GameObjects.Container;
	theme: LaneChooserGameTheme;
	renderWidth: number;
	renderHeight: number;
	viewportWidth: number;
	renderScaleX: number;
	renderScale: number;
};

function getColorValue(color: string): number {
	return Phaser.Display.Color.HexStringToColor(color).color;
}

function getNormalizedProgress(durationMs: number, delayMs: number): number {
	if (durationMs <= 0) {
		return 0;
	}

	const elapsedMs = -delayMs;
	const wrappedElapsed = ((elapsedMs % durationMs) + durationMs) % durationMs;

	return wrappedElapsed / durationMs;
}

export class SkyController {
	private readonly scene: Phaser.Scene;
	private readonly container: Phaser.GameObjects.Container;
	private readonly renderWidth: number;
	private readonly renderHeight: number;
	private readonly viewportWidth: number;
	private readonly renderScaleX: number;
	private readonly clouds: SkyCloudRuntime[] = [];

	constructor({
		scene,
		container,
		theme,
		renderWidth,
		renderHeight,
		viewportWidth,
		renderScaleX,
		renderScale,
	}: SkyControllerConfig) {
		this.scene = scene;
		this.container = container;
		this.renderWidth = renderWidth;
		this.renderHeight = renderHeight;
		this.viewportWidth = viewportWidth;
		this.renderScaleX = renderScaleX;

		const sun = this.scene.add.circle(
			(this.renderWidth * theme.sky.sun.xPct) / 100,
			(this.renderHeight * theme.sky.sun.yPct) / 100,
			(theme.sky.sun.diameterPx * renderScale) / 2,
			getColorValue(theme.sky.sun.color),
			theme.sky.sun.alpha ?? 1,
		);
		this.container.add(sun);

		theme.sky.clouds.forEach((cloudConfig) => {
			const cloud = this.createCloud(cloudConfig);
			if (!cloud) {
				return;
			}

			this.clouds.push(cloud);
			this.container.add(cloud.sprite);
		});
	}

	update(delta: number) {
		this.clouds.forEach((cloud) => {
			if (!cloud.sprite.active || cloud.durationMs <= 0) {
				return;
			}

			cloud.progress = (cloud.progress + delta / cloud.durationMs) % 1;
			cloud.sprite.x = Phaser.Math.Linear(cloud.startX, cloud.endX, cloud.progress);
		});
	}

	destroy() {
		this.container.removeAll(true);
		this.clouds.length = 0;
	}

	private createCloud(cloudConfig: LaneChooserSkyCloud): SkyCloudRuntime | null {
		const texture = this.scene.textures.get(cloudConfig.textureKey);
		if (!texture || !texture.getSourceImage()) {
			return null;
		}

		const sourceImage = texture.getSourceImage() as {
			width: number;
			height: number;
		};
		if (!sourceImage.width || !sourceImage.height) {
			return null;
		}

		const cloudWidthPx = Phaser.Math.Clamp(
			(this.viewportWidth * cloudConfig.widthVw) / 100,
			cloudConfig.minWidthPx,
			cloudConfig.maxWidthPx,
		);
		const displayWidth = cloudWidthPx * this.renderScaleX;
		const displayHeight = (displayWidth * sourceImage.height) / sourceImage.width;
		const travelPadding = this.renderWidth * 0.1;
		const startX =
			cloudConfig.direction === 'right'
				? -displayWidth / 2 - travelPadding
				: this.renderWidth + displayWidth / 2 + travelPadding;
		const endX =
			cloudConfig.direction === 'right'
				? this.renderWidth + displayWidth / 2 + travelPadding
				: -displayWidth / 2 - travelPadding;
		const progress = getNormalizedProgress(cloudConfig.durationMs, cloudConfig.delayMs);
		const cloud = this.scene.add.image(
			Phaser.Math.Linear(startX, endX, progress),
			(this.renderHeight * cloudConfig.topPct) / 100,
			cloudConfig.textureKey,
		);

		cloud.setOrigin(0.5, 0.5);
		cloud.setDisplaySize(displayWidth, displayHeight);
		cloud.setAlpha(cloudConfig.alpha ?? 1);
		if (cloudConfig.tint) {
			cloud.setTint(getColorValue(cloudConfig.tint));
		}

		return {
			sprite: cloud,
			startX,
			endX,
			durationMs: cloudConfig.durationMs,
			progress,
		};
	}
}
